import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/taches`;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");

  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

const buildQueryParams = (filters = {}) => {
  const params = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      value !== "undefined"
    ) {
      params[key] = value;
    }
  });

  return params;
};

const handleServiceError = (error, fallbackMessage) => {
  const apiMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallbackMessage;

  console.error(fallbackMessage, error);
  throw new Error(apiMessage);
};

/// GET

// Récupérer toutes les tâches avec filtres optionnels
// Exemples de filtres :
// getAllTasks({ q: "urgent", statut: "A faire", priority: "URGENT", archived: "false" })
export const getAllTasks = async (filters = {}) => {
  try {
    const response = await axios.get(`${apiUrl}/allTasks`, {
      ...getAuthHeaders(),
      params: buildQueryParams(filters),
    });

    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Erreur lors du chargement des tâches :", error);
    return [];
  }
};

// Récupérer le tableau Kanban complet
// Colonnes = applications
// Cartes = tâches
// Exemple : getKanbanBoard({ q: "MonAppliClub", archived: "false" })
export const getKanbanBoard = async (filters = {}) => {
  try {
    const response = await axios.get(`${apiUrl}/kanban`, {
      ...getAuthHeaders(),
      params: buildQueryParams(filters),
    });

    return (
      response.data || {
        board: null,
        stats: {
          total: 0,
          urgent: 0,
          done: 0,
          late: 0,
        },
        columns: [],
      }
    );
  } catch (error) {
    console.error("Erreur lors du chargement du tableau Kanban :", error);

    return {
      board: null,
      stats: {
        total: 0,
        urgent: 0,
        done: 0,
        late: 0,
      },
      columns: [],
    };
  }
};

// Compter les tâches non terminées
export const countTasksNotDone = async () => {
  try {
    const response = await axios.get(
      `${apiUrl}/countTasksNotDone`,
      getAuthHeaders()
    );

    return response.data || { count: 0 };
  } catch (error) {
    console.error("Erreur lors du chargement des tâches non terminées :", error);
    return { count: 0 };
  }
};

// Récupérer une tâche par son ID
export const getTaskById = async (id) => {
  try {
    if (!id) {
      throw new Error("ID de tâche manquant.");
    }

    const response = await axios.get(
      `${apiUrl}/taskById/${id}`,
      getAuthHeaders()
    );

    return response.data || null;
  } catch (error) {
    handleServiceError(error, "Erreur lors du chargement de la tâche.");
  }
};

/// POST

// Ajouter une tâche
export const addTask = async (payload = {}) => {
  try {
    const response = await axios.post(
      `${apiUrl}/addTask`,
      payload,
      getAuthHeaders()
    );

    return response.data;
  } catch (error) {
    handleServiceError(error, "Erreur lors de la sauvegarde de la tâche.");
  }
};

// Dupliquer une tâche
export const duplicateTask = async (id) => {
  try {
    if (!id) {
      throw new Error("ID de tâche manquant.");
    }

    const response = await axios.post(
      `${apiUrl}/duplicateTask/${id}`,
      {},
      getAuthHeaders()
    );

    return response.data;
  } catch (error) {
    handleServiceError(error, "Erreur lors de la duplication de la tâche.");
  }
};

/// PUT

// Modifier une tâche complète
export const updateTask = async (id, payload = {}) => {
  try {
    if (!id) {
      throw new Error("ID de tâche manquant.");
    }

    const response = await axios.put(
      `${apiUrl}/updateTask/${id}`,
      payload,
      getAuthHeaders()
    );

    return response.data;
  } catch (error) {
    handleServiceError(error, "Erreur lors de la mise à jour de la tâche.");
  }
};

/// PATCH

// Déplacer une tâche dans le Kanban
// payload attendu :
// {
//   product_app_id: 1,
//   position: 2000
// }
export const moveTask = async (id, payload = {}) => {
  try {
    if (!id) {
      throw new Error("ID de tâche manquant.");
    }

    const response = await axios.patch(
      `${apiUrl}/moveTask/${id}`,
      payload,
      getAuthHeaders()
    );

    return response.data;
  } catch (error) {
    handleServiceError(error, "Erreur lors du déplacement de la tâche.");
  }
};

// Archiver une tâche
export const archiveTask = async (id) => {
  try {
    if (!id) {
      throw new Error("ID de tâche manquant.");
    }

    const response = await axios.patch(
      `${apiUrl}/archiveTask/${id}`,
      {},
      getAuthHeaders()
    );

    return response.data;
  } catch (error) {
    handleServiceError(error, "Erreur lors de l'archivage de la tâche.");
  }
};

// Restaurer une tâche archivée
export const restoreTask = async (id) => {
  try {
    if (!id) {
      throw new Error("ID de tâche manquant.");
    }

    const response = await axios.patch(
      `${apiUrl}/restoreTask/${id}`,
      {},
      getAuthHeaders()
    );

    return response.data;
  } catch (error) {
    handleServiceError(error, "Erreur lors de la restauration de la tâche.");
  }
};

// Basculer une tâche en Terminée / A faire
export const toggleTaskDone = async (id) => {
  try {
    if (!id) {
      throw new Error("ID de tâche manquant.");
    }

    const response = await axios.patch(
      `${apiUrl}/toggleTaskDone/${id}`,
      {},
      getAuthHeaders()
    );

    return response.data;
  } catch (error) {
    handleServiceError(error, "Erreur lors du changement de statut de la tâche.");
  }
};

/// DELETE

// Supprimer définitivement une tâche
export const deleteTask = async (id) => {
  try {
    if (!id) {
      throw new Error("ID de tâche manquant.");
    }

    const response = await axios.delete(
      `${apiUrl}/deleteTask/${id}`,
      getAuthHeaders()
    );

    return response.data;
  } catch (error) {
    handleServiceError(error, "Erreur lors de la suppression de la tâche.");
  }
};